import { IToggleInput } from '@models/IToggleInput';
import classNames from 'classnames';
import { motion } from 'framer-motion';

const ToggleInput = ({
  checked = false,
  dataTestId = 'Toggle-Input',
  name,
  label,
  labelClassNames,
  uncheckedLabel,
  checkedLabel,
  onChange,
  onBlur,
  isWrappedInput,
  ...inputProps
}: IToggleInput) => {
  const { disabled, required } = inputProps;

  const handleToggle = () => {
    onChange?.(!checked);
  };

  return (
    <div
      className={classNames(
        'flex',
        isWrappedInput ? 'flex-col items-start' : 'items-center space-x-2'
      )}
    >
      {label && (
        <label
          className={classNames(
            disabled ? 'pointer-events-none cursor-default' : 'cursor-pointer',
            labelClassNames || 'body-four'
          )}
          data-testid={`${dataTestId}-Label`}
          htmlFor={name}
        >
          {required ? `${label} *` : label}
        </label>
      )}

      <div className="flex items-center space-x-2.5">
        {uncheckedLabel && (
          <div
            className="body-four"
            data-testid={`${dataTestId}-Unchecked-Label`}
          >
            {uncheckedLabel}
          </div>
        )}

        <div
          className={classNames(
            disabled ? 'pointer-events-none cursor-default' : 'cursor-pointer',
            'relative h-[19px] w-9'
          )}
        >
          <input
            id={name}
            {...inputProps}
            className="absolute inset-0 cursor-pointer opacity-0"
            checked={checked}
            onChange={handleToggle}
            onBlur={onBlur}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                handleToggle();
              }
            }}
            aria-label={checked ? checkedLabel : uncheckedLabel}
            type="checkbox"
            data-testid={`${dataTestId}-Checkbox`}
          />

          <div
            className={classNames(
              checked
                ? 'bg-black-100 dark:bg-yellow'
                : 'bg-grey dark:bg-grey-500',
              disabled ? 'opacity-50' : 'opacity-100',
              'pointer-events-none absolute left-0 top-0 block h-[19px] w-9 rounded-full transition-colors duration-200'
            )}
          />

          <motion.span
            animate={checked ? 'checked' : 'unchecked'}
            className={classNames(
              checked
                ? 'bg-yellow dark:bg-black-100'
                : 'bg-white dark:bg-grey-100',
              disabled ? 'opacity-50' : 'opacity-100',
              'pointer-events-none absolute left-0 top-[2px] h-[15px] w-[15px] rounded-full transition-colors duration-200'
            )}
            initial={checked ? 'checked' : 'unchecked'}
            transition={{
              duration: 0.2,
              mass: 0.4,
              stiffness: 80,
              type: 'spring',
            }}
            variants={{
              checked: { x: '19px' },
              unchecked: { x: '2px' },
            }}
          />
        </div>

        {checkedLabel && (
          <div
            className="body-four"
            data-testid={`${dataTestId}-Checked-Label`}
          >
            {checkedLabel}
          </div>
        )}
      </div>
    </div>
  );
};

export default ToggleInput;
